import { setItem, usePureEffect } from "@buzzeasy/shared-frontend-utilities";
import { jwtDecode } from "jwt-decode";
import { User } from "oidc-client-ts";
import { PropsWithChildren, ReactElement, useCallback, useContext, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import AuthenticatingScreen from "../../components/presenters/AuthenticatingScreen";
import AuthenticationFailedScreen from "../../components/presenters/AuthenticationFailedScreen";
import NoAccessScreen from "../../components/presenters/NoAccessScreen";
import { useLogoutMutation } from "../../redux/apis/blenderApi";
import { selectAuthInfo, setAuthInfo } from "../../redux/slices/authSlice";
import { useAppDispatch } from "../../redux/store";
import { AuthContext, AuthState, preferredLoginTenantKey } from "./AuthProvider";
import { hasAuthParams } from "./AuthProvider.helpers";
import { UserManagerContext } from "./UserManagerProvider";

/**
 * Handles authentication using the redirect method. Easy and convenient method that should be used where redirect is allowed.
 *
 * **Provider dependencies:**
 * - UserManagerProvider
 * - ThemeProviders
 */

export default function RedirectAuthHandler({ children }: PropsWithChildren): ReactElement {
  const userManager = useContext(UserManagerContext);
  const { token } = useSelector(selectAuthInfo);

  const [authState, setAuthState] = useState<AuthState>("notAuthenticated");
  const [user, setUser] = useState<User | undefined>(undefined);

  const appDispatch = useAppDispatch();

  const handleLoggedIn = useCallback(
    (u: User) => {
      setAuthState("authenticated");
      setUser(u);

      const tenantId = jwtDecode<{ tenant: string }>(u.access_token).tenant;
      setItem(preferredLoginTenantKey, tenantId);

      appDispatch(setAuthInfo({
        token: u.access_token,
        tenantId,
        user: {
          id: u.profile.sub,
          name: u.profile.name,
          email: u.profile.email,
          preferredUsername: u.profile.preferred_username,
        },
      }));

      window.history.replaceState(null, "_unused_", "/");
    },
    [appDispatch],
  );

  const startLogin = useCallback(
    async () => {
      setAuthState("inProgress");

      try {
        const existingUser = await userManager.getUser();

        if (!existingUser || existingUser.expired)
          await userManager.signinRedirect();
        else
          handleLoggedIn(existingUser);
      }
      catch {
        setAuthState("failed");
      }
    },
    [handleLoggedIn, userManager],
  );

  const handleLoginCallback = useCallback(
    async () => {
      try {
        const createdUser = await userManager.signinCallback();

        if (createdUser)
          handleLoggedIn(createdUser);
        else
          setAuthState("failed");
      }
      catch {
        setAuthState("failed");
      }
    },
    [handleLoggedIn, userManager],
  );

  usePureEffect(
    (deps) => {
      if (hasAuthParams()) {
        void deps.handleLoginCallback();
      }
      else if (!user && authState === "notAuthenticated") {
        void deps.startLogin();
      }
    },
    [authState, user],
    { handleLoginCallback, startLogin },
  );

  const [logoutOfBlender] = useLogoutMutation();

  const logout = useCallback(
    () => {
      void logoutOfBlender();
      void userManager.signoutRedirect();
    },
    [logoutOfBlender, userManager],
  );

  // to make contextValue static
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  const contextValue = useMemo(
    () => ({ logout: logoutRef.current }),
    [],
  );

  if (authState === "failed")
    return <AuthenticationFailedScreen onTryAgainClick={startLogin} />;
  if (authState === "inProgress" || !user || !token)
    return <AuthenticatingScreen />;
  if (!user?.scopes.includes("bp.access"))
    return <NoAccessScreen />;

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}
